// Library imports.
import "normalize.css";
import "../../css/main.css";

// React imports.
import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";

// App imports.
import BaseLayout from "./components/BaseLayout";
import * as worker from "../baseWorker";

// Create a ref for the BaseLayout component
const baseComponentRef = React.createRef<BaseLayout>();

// Define functions that will use the component once it's available
async function getUserInfo(component) {
     const userWorker: worker.baseWorker = new worker.baseWorker();
     try {
          const currentUser: worker.IUserInfo = await userWorker.getUserInfo();
          component.state.setCurrentUserInfo(currentUser);
     }
     catch (err) {
          console.log(err);
          component.state.showError(err.message);
          throw Error("Failed to get user info.");
     }
}

async function getGrantees(component) {
     const granteeWorker: worker.baseWorker = new worker.baseWorker();
     try {
          const grantees: worker.IGrantee[] = await granteeWorker.getGrantees();
          component.state.setGrantees(grantees);
     }
     catch (err) {
          console.log(err);
          component.state.showError(err.message);
          throw Error("Failed to get grantees.");
     }
}

// Ensure token is fetched before making API calls
async function ensureToken() {
    try {
        // Don't clear session storage as it might contain the JWT token
        const token = sessionStorage.getItem('bearerToken');
        if (!token) {
            const response = await axios.post('/token.php');
            const newToken = response.data.token;
            if (newToken) {
                sessionStorage.setItem('bearerToken', newToken);
                console.log('Token initialized successfully');
            }
        }
        return true;
    } catch (error) {
        console.error('Failed to initialize token:', error);
        return false;
    }
}

// React 18+: createRoot renders asynchronously. Poll for the ref to be populated
// before bootstrapping (a single setTimeout(0) can fire before commit).
function waitForMount(attempt = 0) {
    if (!baseComponentRef.current) {
        if (attempt > 50) {
            console.error("BaseLayout component not mounted yet");
            return;
        }
        setTimeout(() => waitForMount(attempt + 1), 20);
        return;
    }
    initializeApp();
}

// Initialize application after component is mounted
async function initializeApp() {
    const baseComponent = baseComponentRef.current!;
    
    // Display the wait message
    baseComponent.state.showSpinner();
    
    try {
        // First ensure we have a valid token
        await ensureToken();
        
        // Then load data - these API calls will use the token
        const granteesPromise = getGrantees(baseComponent);
        const userPromise = getUserInfo(baseComponent);
        
        await Promise.all([granteesPromise, userPromise]);
        baseComponent.state.hideSpinner();
    } catch (error) {
        console.error("Failed to initialize app:", error);
        baseComponent.state.hideSpinner();
        baseComponent.state.showError("Failed to load application data");
    }
}

// Render the component using React 18 createRoot API
const root = ReactDOM.createRoot(document.getElementById("app"));
root.render(
    <BaseLayout ref={baseComponentRef} />
);

// Initialize app after component is mounted
waitForMount();
